// The fit-check button's embed code (embed.outfino.io), generated for the
// design picked on the Embed settings page.
//
// Mirrors the embedded-app repo (Outfino/embedded-app): the theme app block
// (shopify-extension/.../fitcheck_button.liquid) and the manual snippet
// (snippet/iframe-embed.html) — same attributes, same sandbox, same embed.js.
// Change them together.

export const EMBED_ORIGIN = 'https://embed.outfino.io';

export const SHAPES = ['pill', 'rounded', 'square'];
export const THEMES = ['dark', 'light'];
export const VARIANTS = ['compact', 'full'];
export const LANGS = ['auto', 'hu', 'en'];
export const HEIGHTS = [44, 46, 48, 50, 52, 54, 56];

// The defaults: full width (CEO decision) — the button fills the column
// under Add to cart.
export const DEFAULT_DESIGN = Object.freeze({
  shape: 'pill',
  theme: 'dark',
  variant: 'full',
  h: 48,
  lang: 'auto',
});

export const PLATFORMS = ['shopify', 'html'];

// The one sandbox / allow pair for every iframe the portal writes or shows —
// byte-for-byte as in the embed repo (snippet/shopify-custom-liquid.liquid,
// snippet/iframe-embed.html). allow-storage-access-by-user-activation +
// storage-access: after sign-in the frame asks for Storage Access, so the
// Outfino session carries over to other shops.
export const IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-storage-access-by-user-activation';
export const IFRAME_ALLOW = 'storage-access; identity-credentials-get';

const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

/// The design with every field clamped to what button.js accepts.
export function normalizeDesign(design = {}) {
  let h = parseInt(design.h, 10);
  h = Number.isFinite(h) ? Math.max(44, Math.min(56, Math.round(h / 2) * 2)) : DEFAULT_DESIGN.h;
  return {
    shape: pick(design.shape, SHAPES, DEFAULT_DESIGN.shape),
    theme: pick(design.theme, THEMES, DEFAULT_DESIGN.theme),
    variant: pick(design.variant, VARIANTS, DEFAULT_DESIGN.variant),
    h,
    lang: pick(design.lang, LANGS, DEFAULT_DESIGN.lang),
  };
}

/// The compact pill's starting width before embed.js sets the one the frame
/// reports: the English label is shorter (same numbers as the theme block).
export function compactWidth(lang) {
  return lang === 'en' ? 150 : 200;
}

function frameTitle(lang) {
  if (lang === 'hu') return 'Outfino – Nézd meg, hogy passzol-e hozzád';
  if (lang === 'en') return 'Outfino – See if it suits you';
  return 'Outfino';
}

/// button.html query for a design, without the per-page values (key, shop,
/// product …) — what the live previews load.
export function designQuery(design) {
  const d = normalizeDesign(design);
  return `lang=${d.lang}&theme=${d.theme}&variant=${d.variant}&shape=${d.shape}&h=${d.h}`;
}

/// The portal's own previews: `demo=1` — the frame runs without a key (no
/// "nincs beállítva" config card; button.js `skipConfig`) — and
/// `analytics=0`, so a preview is never measured. Never in a copied code:
/// buildEmbedSrc / buildEmbedCode below do not add either.
export const PREVIEW_PARAMS = 'demo=1&analytics=0';

export function previewSrc(design) {
  return `${EMBED_ORIGIN}/button.html?${designQuery(design)}&${PREVIEW_PARAMS}`;
}

/// The id of the webshop-address field on Beállítások (the troubleshooting
/// list links to it).
export const DOMAIN_ANCHOR = 'embed-domain';

/// The webshop address the button may run on (one site per key, and its
/// www. form — nothing else).
/// "https://www.Nova-Atelier.hu/" → { domain: 'nova-atelier.hu' }; a path,
/// port, query or anything that is not a hostname → { error }.
///   error: 'required' | 'path' | 'invalid'
const RESERVED_SUFFIXES = ['localhost', 'local', 'internal', 'test', 'invalid', 'example', 'lan', 'home', 'arpa'];

export function normalizeDomain(raw) {
  let s = String(raw || '').trim().toLowerCase();
  if (!s) return { error: 'required' };
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/\/+$/, '');
  if (/[/?#]/.test(s) || /:\d*$/.test(s)) return { error: 'path' };
  if (/[@\s]/.test(s)) return { error: 'invalid' };
  s = s.replace(/^www\./, '').replace(/\.$/, '');
  let host;
  try {
    host = new URL(`http://${s}`).hostname; // lowercases, IDN → punycode
  } catch {
    return { error: 'invalid' };
  }
  // The names the API refuses as a site: localhost and the reserved TLDs.
  if (RESERVED_SUFFIXES.includes(host.split('.').pop())) return { error: 'invalid' };
  const ok = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:xn--[a-z0-9-]{2,59}|[a-z]{2,63})$/.test(host);
  return ok ? { domain: host } : { error: 'invalid' };
}

/// Stands in for the webshop address in a code shown before it is saved (no
/// such code can be copied — see EmbedSettingsTab's copy lock).
export const SHOP_PLACEHOLDER = 'WEBSHOP_DOMAIN';

/// The page-specific part of the iframe src, per platform. `shop` is the
/// brand's saved webshop address, written in literally (the key is checked
/// against that address only — no Shopify dependency); the product stays a
/// Liquid variable on Shopify and a placeholder elsewhere. The Liquid filters
/// url-encode their own output.
function pageParams(platform, key, domain) {
  const parts = [];
  if (key) parts.push(`key=${encodeURIComponent(key)}`);
  parts.push(`shop=${domain ? encodeURIComponent(domain) : SHOP_PLACEHOLDER}`);
  if (platform === 'shopify') {
    parts.push('product={{ product.id }}');
    parts.push('handle={{ product.handle | url_encode }}');
    return parts.join('&')
      + "{% if product.featured_image %}&img={{ product.featured_image | image_url: width: 800 | prepend: 'https:' | url_encode }}{% endif %}";
  }
  parts.push('product=PRODUCT_ID');
  parts.push('handle=PRODUCT_HANDLE');
  parts.push('img=PRODUCT_IMAGE_URL');
  return parts.join('&');
}

/// The iframe src alone, for a merchant who builds the iframe themselves:
/// Liquid variables on Shopify, placeholders elsewhere. Plain `&` — it is a
/// URL here, not yet an HTML attribute.
export function buildEmbedSrc({ design, key, platform = 'shopify', domain = null } = {}) {
  const p = pick(platform, PLATFORMS, 'shopify');
  return `${EMBED_ORIGIN}/button.html?${designQuery(design)}&${pageParams(p, key, domain)}`;
}

/// The same string with the key masked, for showing on the page.
export function maskKeyIn(text, key) {
  return key ? String(text).split(encodeURIComponent(key)).join(maskKey(key)) : String(text);
}

const COMMENTS = {
  hu: {
    shopify: 'Outfino fit-check gomb – Shopify „Egyéni Liquid” blokk a termékoldalon.',
    html: [
      'Outfino fit-check gomb – a termékoldalra, ahová a gombot szeretnéd.',
      'Cseréld ki: PRODUCT_ID → a termék azonosítója (csak számjegyek),',
      'PRODUCT_HANDLE → a termék URL-neve, PRODUCT_IMAGE_URL → a termékkép https címe URL-kódolva.',
    ],
  },
  en: {
    shopify: 'Outfino fit-check button – Shopify "Custom Liquid" block on the product page.',
    html: [
      'Outfino fit-check button – on the product page, where the button should appear.',
      'Replace: PRODUCT_ID → the product id (digits only),',
      'PRODUCT_HANDLE → the product URL slug, PRODUCT_IMAGE_URL → the product image https URL, URL-encoded.',
    ],
  },
};

/// The full code a merchant pastes: the iframe + embed.js.
///   platform  'shopify' (Custom Liquid, Liquid variables) | 'html' (placeholders)
///   uiLang    the language of the one-line comment on top ('hu' | 'en')
export function buildEmbedCode({ design, key, platform = 'shopify', uiLang = 'hu', domain = null } = {}) {
  const d = normalizeDesign(design);
  const p = pick(platform, PLATFORMS, 'shopify');
  const copy = COMMENTS[uiLang === 'en' ? 'en' : 'hu'];
  const comment = p === 'shopify' ? [copy.shopify] : copy.html;
  const width = d.variant === 'compact' ? `${compactWidth(d.lang)}px` : '100%';
  // Inside the HTML attribute every `&` is written `&amp;`.
  const src = buildEmbedSrc({ design: d, key, platform: p, domain }).replace(/&/g, '&amp;');

  return [
    `<!-- ${comment.join('\n     ')} -->`,
    '<iframe',
    '  data-outfino-fitcheck',
    `  data-variant="${d.variant}"`,
    `  src="${src}"`,
    `  title="${frameTitle(d.lang)}"`,
    '  loading="lazy"',
    '  scrolling="no"',
    '  referrerpolicy="no-referrer"',
    `  sandbox="${IFRAME_SANDBOX}"`,
    `  allow="${IFRAME_ALLOW}"`,
    `  style="display: block; border: 0; background: transparent; color-scheme: normal; overflow: hidden; width: ${width}; height: ${d.h}px;"`,
    '></iframe>',
    `<script src="${EMBED_ORIGIN}/embed.js" defer></script>`,
  ].join('\n');
}

/// A real publishable key: ofk_live_ / ofk_test_ + 32 letters or digits.
/// Anything else is never put into a code to copy.
export const KEY_PATTERN = /^ofk_(live|test)_[A-Za-z0-9]{32}$/;
export const isValidKey = (key) => typeof key === 'string' && KEY_PATTERN.test(key);

/// "ofk_live_3f9a…c2b7" → "ofk_live_••••••••c2b7": the prefix and the last
/// four characters, enough to tell two keys apart.
export function maskKey(key) {
  if (typeof key !== 'string' || !key) return '';
  const m = /^(ofk_(?:live|test)_)(.+)$/.exec(key);
  const prefix = m ? m[1] : '';
  const rest = m ? m[2] : key;
  if (rest.length <= 4) return `${prefix}••••`;
  return `${prefix}${'•'.repeat(8)}${rest.slice(-4)}`;
}
